/**
 * DM/Messenger entry point — dropdown with conversations and unread count.
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { MessageCircle } from 'lucide-react';

function ConversationRow({
  otherUserId,
  lastMessage,
  unreadCount,
  isActive,
  onSelect,
}: {
  otherUserId: Id<'users'>;
  lastMessage: { content: string; createdAt: number; read: boolean };
  unreadCount: number;
  isActive: boolean;
  onSelect: () => void;
}) {
  const user = useQuery(api.users.getById, { id: otherUserId });
  const displayName = user?.name ?? user?.email ?? 'Unknown';
  const preview =
    lastMessage.content.length > 40
      ? lastMessage.content.slice(0, 40) + '…'
      : lastMessage.content;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors ${
        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{displayName}</span>
          {unreadCount > 0 && (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded-full bg-accent text-accent-foreground">
              {unreadCount}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{preview}</p>
      </div>
    </button>
  );
}

function MessagesPanel({
  otherUserId,
  onClose,
}: {
  otherUserId: Id<'users'>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState('');
  const messages = useQuery(api.messages.listMessagesWith, { otherUserId });
  const markRead = useMutation(api.messages.markConversationRead);
  const send = useMutation(api.messages.send);
  const user = useQuery(api.users.getById, { id: otherUserId });
  const displayName = user?.name ?? user?.email ?? 'Unknown';

  useEffect(() => {
    void markRead({ otherUserId });
  }, [otherUserId, markRead]);

  const handleSend = () => {
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    void send({ recipientId: otherUserId, content: t });
  };

  return (
    <div className="absolute top-full right-0 mt-1 w-[360px] max-h-[420px] flex flex-col rounded-xl border border-border bg-card shadow-xl z-50">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="font-semibold text-sm truncate">{displayName}</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground p-1 rounded"
        >
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px]">
        {messages?.map((m) => (
          <div
            key={m._id}
            className={`flex ${m.senderId === otherUserId ? 'justify-start' : 'justify-end'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                m.senderId === otherUserId
                  ? 'bg-muted text-foreground'
                  : 'bg-primary text-primary-foreground'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {(!messages || messages.length === 0) && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No messages yet. Send one below.
          </p>
        )}
      </div>
      <div className="p-3 border-t border-border flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Type a message…"
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!draft.trim()}
          className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>
    </div>
  );
}

export function MessagesDropdown() {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<Id<'users'> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const conversations = useQuery(api.messages.listConversations, {});
  const totalUnread = conversations?.reduce((s, c) => s + c.unreadCount, 0) ?? 0;

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSelectedUserId(null);
      }
    };
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        aria-label="Messages"
      >
        <MessageCircle className="h-5 w-5" />
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-medium">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <>
          {selectedUserId ? (
            <MessagesPanel
              otherUserId={selectedUserId}
              onClose={() => setSelectedUserId(null)}
            />
          ) : (
            <div className="absolute top-full right-0 mt-1 w-[320px] max-h-[380px] flex flex-col rounded-xl border border-border bg-card shadow-xl z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="font-semibold text-sm">Messages</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Your conversations
                </p>
              </div>
              <div className="overflow-y-auto p-2 max-h-[300px]">
                {conversations?.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    No conversations yet
                  </p>
                )}
                {conversations?.map((c) => (
                  <ConversationRow
                    key={c.otherUserId}
                    otherUserId={c.otherUserId as Id<'users'>}
                    lastMessage={c.lastMessage}
                    unreadCount={c.unreadCount}
                    isActive={false}
                    onSelect={() => setSelectedUserId(c.otherUserId as Id<'users'>)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
